import { STAFF_ROLES, STAFF_STATUS } from '../constants/staff';

/**
 * labTechnicianRoster — the ONE selector every lab-technician picker reads
 * (`OrderLabDialog`, `RegisterVisitForm`, `LabAssignPanel`) so the three
 * surfaces can never drift on "what counts as a lab technician"
 * (`feedback_single_source_of_truth_derived_values`,
 * `feedback_use_shared_role_status_constants`).
 *
 * Pure — no fetching. Callers own their own `fetchStaffList()` call/cache;
 * this module only normalizes + filters + sorts whatever roster shape comes
 * back. `fetchStaffList()` itself can resolve as `{success, data}`, a bare
 * array (some call sites pre-unwrap it), `{staff: [...]}` or
 * `{members: [...]}` — every shape actually seen in this codebase.
 */

function _normalizeRosterList(rosterResponse) {
  if (Array.isArray(rosterResponse)) return rosterResponse;
  if (rosterResponse && typeof rosterResponse === 'object') {
    if (Array.isArray(rosterResponse.data)) return rosterResponse.data;
    if (Array.isArray(rosterResponse.staff)) return rosterResponse.staff;
    if (Array.isArray(rosterResponse.members)) return rosterResponse.members;
  }
  // Covers a failed `{success:false, error}` response and any other
  // unrecognized shape — an empty roster, never a thrown error.
  return [];
}

/** `member_id`, falling back to `id` (some fixtures/mocks use the latter). */
export function technicianId(m) {
  return m.member_id || m.id;
}

/** Display name — `name`, then "first last", then the id itself (lifted
 * verbatim from `LabAssignPanel`'s local `memberName`). */
export function technicianName(m) {
  return m.name || [m.first_name, m.last_name].filter(Boolean).join(' ').trim() || technicianId(m);
}

/**
 * Normalize a `fetchStaffList()` response into the lab-technician roster:
 * every member whose role is `LAB_TECHNICIAN` AND whose status is `ACTIVE`
 * (or has no `status` field at all — a legacy row, kept visible rather than
 * dropped by an empty-set filter), sorted by display name so a picker's
 * option order is stable across opens.
 *
 * D3 — `fetchStaffList()` returns members regardless of status (the same
 * response `ManageStaffPage` reads to show DISABLED rows), so a deactivated
 * technician must be excluded HERE, not assumed absent from the response.
 * The backend mirrors this rule server-side (422 `TECHNICIAN_NOT_FOUND` for
 * a non-ACTIVE assignee).
 * @param {*} rosterResponse
 * @returns {Array<Object>} the original member objects (untouched), filtered + sorted
 */
export function selectLabTechnicians(rosterResponse) {
  const list = _normalizeRosterList(rosterResponse);
  return list
    .filter((m) => String(m.role || '').toUpperCase() === STAFF_ROLES.LAB_TECHNICIAN)
    .filter((m) => !m.status || m.status === STAFF_STATUS.ACTIVE)
    .sort((a, b) => technicianName(a).localeCompare(technicianName(b)));
}
